/**
 * CORE-F — loading the heatmap's authoritative inputs
 *
 * Five dimensions from four tables plus CORE-E's second store, each read
 * independently and each reporting its own load state. That independence is
 * the point: when the diagnostic table is unreachable, proficiency and
 * training readiness still render, and diagnostic reads "unavailable"
 * rather than disappearing or — far worse — reading as zero.
 *
 * The two proficiency stores
 * --------------------------
 * CORE-E keeps two representations, by documented design rather than drift:
 *
 *   `technician_proficiencies`   portable, per profile × category, carries
 *                                confidence and sample size, derived from
 *                                completed work. This is what a Passport is
 *                                about, so it is ALWAYS read where a
 *                                profile id exists.
 *   `technicians.proficiencies`  the current shop's scheduling map. No
 *                                confidence, no sample size, no timestamp.
 *
 * An earlier version of this hook did `const hasOverlay = !!liveProficiencies`
 * and skipped the portable query entirely when that was true. `{}` is
 * truthy, and `TechnicianForm` treats an empty proficiency map as an
 * ordinary state — so a technician at a shop that had set no scheduling
 * values lost every portable row they had, and their whole heatmap read as
 * "no data". A partial map did the same thing to whichever categories it
 * did not mention. Both reads now happen independently, and the scheduling
 * map is consulted per category rather than as an all-or-nothing switch.
 *
 * This hook widens no audience. It reads exactly the tables the Passport
 * surfaces already read for this technician, under the same RLS, keyed by
 * the same identifiers those surfaces already resolve. CORE-Q owns who may
 * see a Passport; CORE-F only decides how the contents are drawn.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
// Core-100 R4 wave 3 (CORE-F Rel): a denied read is not an empty one.
import { fetchPassportSectionReadAccess } from '@/lib/passport/sectionReadAccess';
import {
  CATEGORY_KEY_TO_PROFICIENCY,
  PROFICIENCY_CATEGORIES,
} from '@/constants/proficiencyCategories';
import {
  aggregateJobStatsByCategory,
  projectHeatmap,
  type DiagnosticRowLite,
  type JobLearningStatRowLite,
  type ProficiencyRowLite,
  type ReadinessRowLite,
} from '@/lib/passport/skillHeatmap/project';
import type {
  HeatmapCell,
  HeatmapSourceStates,
  SourceStatus,
} from '@/lib/passport/skillHeatmap/types';

/**
 * Map whatever the writer stored into the proficiency taxonomy.
 *
 * The learning-stats trigger writes `COALESCE(proficiency_category,
 * category)`, so a row may hold either a display name or a raw catalog key.
 * Anything that resolves to neither returns `null` and is counted as
 * unattributed rather than guessed at — attaching a technician's completed
 * work to the wrong category would be worse than admitting we cannot place
 * it.
 */
export function normaliseJobCategory(raw: string): string | null {
  if (PROFICIENCY_CATEGORIES.includes(raw)) return raw;
  return CATEGORY_KEY_TO_PROFICIENCY[raw] ?? null;
}

/**
 * Keep only the numeric entries of a scheduling map.
 *
 * A non-numeric entry is not a zero and not a reason to discard the key's
 * neighbours; it simply contributes no claim.
 */
export function numericScheduling(
  map: Record<string, unknown> | null | undefined,
): Record<string, number> | null {
  if (!map || typeof map !== 'object') return null;
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(map)) {
    if (typeof value === 'number' && Number.isFinite(value)) out[key] = value;
  }
  return out;
}

function statusOf(
  enabled: boolean,
  isLoading: boolean,
  isError: boolean,
): SourceStatus {
  if (!enabled) return 'not_linked';
  if (isError) return 'error';
  if (isLoading) return 'loading';
  return 'ok';
}

/**
 * Core-100 R4 wave 3 (CORE-F Rel): combine a source's own read state with the
 * server's statement of whether this viewer may read it at all. RLS answers a
 * denied read with zero rows, so without the access answer a denial would
 * render as "no evidence". A failed access read is a failure of every source
 * it governs (never a guessed permission).
 */
export function gatedStatus(
  own: SourceStatus,
  access: { enabled: boolean; isLoading: boolean; isError: boolean; permitted: boolean | undefined },
): SourceStatus {
  if (own === 'not_linked') return own;
  // No Passport profile to ask about: nothing to gate (the profile sources are already `not_linked`).
  if (!access.enabled) return own;
  if (access.isError) return 'error';
  if (access.isLoading || access.permitted === undefined) return own === 'error' ? 'error' : 'loading';
  if (!access.permitted) return 'not_permitted';
  return own;
}

export interface UseSkillHeatmapDataArgs {
  technicianProfileId: string | undefined;
  /**
   * The live `technicians.id`, when the Passport surface has already
   * resolved and verified one. Absent means the job-stats source is
   * `not_linked` — not empty, and not zero.
   */
  liveShopTechnicianId?: string | null;
  /**
   * The already-verified `technicians.proficiencies` map, when the surface
   * is showing a live shop record. Passed in rather than re-fetched so this
   * hook cannot bypass the link verification the surface performs.
   *
   * `{}` is a legitimate value meaning "this shop has set no scheduling
   * proficiencies". It must never suppress the portable record.
   */
  liveProficiencies?: Record<string, number> | null;
  /** Injected for deterministic recency under test. */
  nowIso?: string;
}

export interface SkillHeatmapData {
  cells: HeatmapCell[];
  sources: HeatmapSourceStates;
  /** Completed-work rows that could not be placed in any category. */
  unattributedJobRows: number;
}

export function useSkillHeatmapData({
  technicianProfileId,
  liveShopTechnicianId,
  liveProficiencies,
  nowIso,
}: UseSkillHeatmapDataArgs): SkillHeatmapData {
  const profileEnabled = !!technicianProfileId;
  const jobStatsEnabled = !!liveShopTechnicianId;
  // Present, including when empty. Only `null`/`undefined` means "this
  // surface resolved no live shop record at all".
  const hasSchedulingMap = liveProficiencies != null;

  // ALWAYS read the portable record where a profile exists. The scheduling
  // map is additional context, never a substitute.
  const proficiencyQuery = useQuery({
    queryKey: ['core-f-proficiencies', technicianProfileId],
    queryFn: async (): Promise<ProficiencyRowLite[]> => {
      const { data, error } = await supabase
        .from('technician_proficiencies')
        .select(
          // Core-100 R4 (CORE-F Prov): the class is read so a baseline never renders like demonstrated work.
          'category_key, proficiency_score, confidence_score, sample_size, updated_at, provenance_class',
        )
        .eq('technician_profile_id', technicianProfileId!);
      if (error) throw error;
      return data ?? [];
    },
    enabled: profileEnabled,
  });

  const diagnosticQuery = useQuery({
    queryKey: ['core-f-diagnostics', technicianProfileId],
    queryFn: async (): Promise<DiagnosticRowLite[]> => {
      const { data, error } = await supabase
        .from('technician_diagnostic_stats')
        .select(
          'category_key, diagnostic_competency_score, total_diagnostics, last_updated_at',
        )
        .eq('technician_profile_id', technicianProfileId!);
      if (error) throw error;
      return data ?? [];
    },
    enabled: profileEnabled,
  });

  const readinessQuery = useQuery({
    queryKey: ['core-f-readiness', technicianProfileId],
    queryFn: async (): Promise<ReadinessRowLite[]> => {
      const { data, error } = await supabase
        .from('technician_soft_proficiencies')
        .select(
          // CORE-E-GAP-006: `validated_at` travels with the boost so the
          // projection can tell an outstanding readiness from a discharged one.
          'category_key, soft_score, training_completions, last_training_at, validated_at',
        )
        .eq('technician_profile_id', technicianProfileId!);
      if (error) throw error;
      // `src/integrations/supabase/types.ts` is generated and predates the
      // CORE-E-GAP-006 column, so the row type is narrowed here rather than
      // regenerating the whole file as a side effect of this slice.
      return (data ?? []) as unknown as ReadinessRowLite[];
    },
    enabled: profileEnabled,
  });

  const jobStatsQuery = useQuery({
    queryKey: ['core-f-job-stats', liveShopTechnicianId],
    queryFn: async (): Promise<JobLearningStatRowLite[]> => {
      const { data, error } = await supabase
        .from('technician_job_learning_stats')
        // `avg_actual_minutes` is what makes an exact category roll-up
        // possible; without it efficiency could only be approximated.
        .select('job_category, avg_efficiency, avg_actual_minutes, jobs_completed')
        .eq('technician_id', liveShopTechnicianId!);
      if (error) throw error;
      return data ?? [];
    },
    enabled: jobStatsEnabled,
  });

  // Core-100 R4 wave 3: which of these sources the viewer may read at all.
  const accessQuery = useQuery({
    queryKey: ['core-f-section-access', technicianProfileId, liveShopTechnicianId ?? null],
    queryFn: () => fetchPassportSectionReadAccess(technicianProfileId!, liveShopTechnicianId ?? null),
    enabled: profileEnabled,
  });
  const access = accessQuery.data;

  const proficiencyData = proficiencyQuery.data;
  const diagnosticData = diagnosticQuery.data;
  const readinessData = readinessQuery.data;
  const jobStatsData = jobStatsQuery.data;

  return useMemo<SkillHeatmapData>(() => {
    const accessState = (permitted: boolean | undefined) => ({
      enabled: profileEnabled,
      isLoading: accessQuery.isLoading,
      isError: accessQuery.isError,
      permitted,
    });
    const sources: HeatmapSourceStates = {
      proficiency: gatedStatus(statusOf(
        profileEnabled,
        proficiencyQuery.isLoading,
        proficiencyQuery.isError,
      ), accessState(access?.proficiency)),
      // The map arrives already resolved by the surface, so it is `ok` the
      // moment it is present and `not_linked` when the surface has no live
      // shop record to read it from.
      scheduling: hasSchedulingMap ? 'ok' : 'not_linked',
      diagnostic: gatedStatus(statusOf(
        profileEnabled,
        diagnosticQuery.isLoading,
        diagnosticQuery.isError,
      ), accessState(access?.diagnostic)),
      readiness: gatedStatus(statusOf(
        profileEnabled,
        readinessQuery.isLoading,
        readinessQuery.isError,
      ), accessState(access?.readiness)),
      jobStats: gatedStatus(statusOf(
        jobStatsEnabled,
        jobStatsQuery.isLoading,
        jobStatsQuery.isError,
      ), accessState(access?.job_stats)),
    };

    const { byCategory, unattributedRows } = aggregateJobStatsByCategory(
      jobStatsData ?? [],
      normaliseJobCategory,
    );

    const cells = projectHeatmap({
      categories: PROFICIENCY_CATEGORIES,
      sources,
      proficiencies: proficiencyData ?? [],
      scheduling: numericScheduling(liveProficiencies),
      diagnostics: diagnosticData ?? [],
      readiness: readinessData ?? [],
      jobStats: byCategory,
      nowIso: nowIso ?? new Date().toISOString(),
    });

    return { cells, sources, unattributedJobRows: unattributedRows };
  }, [
    hasSchedulingMap,
    liveProficiencies,
    profileEnabled,
    jobStatsEnabled,
    nowIso,
    proficiencyData,
    proficiencyQuery.isLoading,
    proficiencyQuery.isError,
    diagnosticData,
    diagnosticQuery.isLoading,
    diagnosticQuery.isError,
    readinessData,
    readinessQuery.isLoading,
    readinessQuery.isError,
    jobStatsData,
    jobStatsQuery.isLoading,
    jobStatsQuery.isError,
    access,
    accessQuery.isLoading,
    accessQuery.isError,
  ]);
}
